import { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { DataTable } from "../../components/ui/dataTable/DataTable";
import { Pagination } from "../../components/ui/pagination/Pagination";
import { StatusBadge } from "../../components/ui/statusBadge/StatusBadge";
import { EmptyState } from "../../components/ui/emptyState/EmptyState";
import { MOCK_DATA, useMockDataVersion } from "../../data/mockDataStore";
import { relative } from "../../utils/format";
import { TRIP_STATUS_OPTIONS } from "../../constants/tripStatus";
import { deriveTripView } from "../../utils/tripView";
import { TripViewHeader } from "./tripView/TripViewHeader";
import { TripOverview } from "./tripView/TripOverview";
import { TripDatesSidebar } from "./tripView/TripDatesSidebar";
import { TripProgram } from "./tripView/TripProgram";

const PAGE_SIZE = 8;

export function Circuits() {
  const { id } = useParams();
  if (id) return <CircuitFiche id={id} />;
  return <CircuitsList />;
}

function CircuitsList() {
  useMockDataVersion();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [statut, setStatut] = useState("");
  const [etab, setEtab] = useState("");
  const [page, setPage] = useState(1);

  const etabOptions = useMemo(() => {
    const ids = [...new Set(MOCK_DATA.circuits.map((c) => c.etabId))];
    return ids.map((id) => {
      const e = MOCK_DATA.etablissements.find((x) => x.id === id);
      return { id, label: e ? e.nom : id };
    });
  }, []);

  const filtered = useMemo(() => {
    const qq = q.toLowerCase();
    return MOCK_DATA.circuits.filter((c) => {
      if (statut && c.statut !== statut) return false;
      if (etab && c.etabId !== etab) return false;
      if (qq && !(c.nom.toLowerCase().includes(qq) || c.id.toLowerCase().includes(qq) || c.destination.toLowerCase().includes(qq))) return false;
      return true;
    });
  }, [q, statut, etab]);

  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <>
      <div className="page-header">
        <div>
          <div className="breadcrumb">
            <a onClick={() => navigate("/dashboard")}>Dashboard</a>
            <i className="fa-solid fa-chevron-right"></i>Circuits
          </div>
          <h1 className="page-title">Circuits</h1>
          <div className="page-sub">Catalogue des circuits proposés par les partenaires</div>
        </div>
        <div className="page-actions">
          <button className="btn"><i className="fa-solid fa-download"></i> Exporter</button>
        </div>
      </div>

      <div className="card">
        <div className="toolbar">
          <div className="search">
            <i className="fa-solid fa-magnifying-glass"></i>
            <input data-testid="circuit-search" placeholder="Rechercher par référence, nom, destination…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
          </div>
          <select className="select" data-testid="circuit-statut" value={statut} onChange={(e) => { setStatut(e.target.value); setPage(1); }}>
            <option value="">Tous les statuts</option>
            {TRIP_STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select className="select" data-testid="circuit-etab" value={etab} onChange={(e) => { setEtab(e.target.value); setPage(1); }}>
            <option value="">Tous les établissements</option>
            {etabOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
          <div className="filter-chips"><span className="badge outline"><i className="fa-solid fa-filter"></i> {filtered.length} résultat{filtered.length > 1 ? "s" : ""}</span></div>
        </div>

        <DataTable
          columns={[
            { label: "Référence", render: (c) => <span className="mono strong">{c.id}</span> },
            { label: "Nom", render: (c) => <div><div className="strong">{c.nom}</div><div className="muted">{c.destination}</div></div> },
            { label: "Établissement", render: (c) => c.etab },
            { label: "Statut", render: (c) => <StatusBadge statut={c.statut} domain="trip" /> },
            { label: "Modifié", render: (c) => relative(c.modifie) },
          ]}
          rows={paged}
          onRowClick={(c) => navigate("/circuits/" + c.id)}
          rowTestIdPrefix="circuit-row"
        />
        <Pagination total={filtered.length} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} />
      </div>
    </>
  );
}

function CircuitFiche({ id }) {
  useMockDataVersion();
  const navigate = useNavigate();
  const [selectedDateIndex, setSelectedDateIndex] = useState(0);
  const c = MOCK_DATA.circuits.find((x) => x.id === id);
  const etablissement = c ? MOCK_DATA.etablissements.find((e) => e.id === c.etabId) : null;
  const view = useMemo(() => (c ? deriveTripView(c, etablissement) : null), [c, etablissement]);

  if (!c) return <EmptyState title="Circuit introuvable" sub={`Aucun circuit ${id}`} />;

  return (
    <>
      <TripViewHeader
        circuit={c}
        dateId={view.dates[selectedDateIndex]?.id}
        onBack={() => navigate("/circuits")}
      />

      <div style={{ marginBottom: 16 }}>
        <a onClick={() => navigate("/etablissements/" + c.etabId)} style={{ color: "var(--primary)", cursor: "pointer", fontSize: 13 }}>
          <i className="fa-solid fa-building"></i> {c.etab}
        </a>
      </div>

      <div className="detail-grid">
        <div style={{ display: "grid", gap: 16 }}>
          <TripOverview circuit={c} view={view} />
          <TripProgram view={view} selectedDate={view.dates[selectedDateIndex]} />
        </div>

        <TripDatesSidebar view={view} selectedDateIndex={selectedDateIndex} onSelectDate={setSelectedDateIndex} />
      </div>
    </>
  );
}

export default Circuits;
